
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type UserRole = 'admin' | 'coordinator' | 'customer';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  allowedRoles, 
  fallbackPath = '/dashboard' 
}) => {
  const { user, loading, hasRole, userRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-juv-cream-white">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-juv-terracotta"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user has any of the allowed roles
  const hasRequiredRole = allowedRoles.some(role => hasRole(role));
  
  if (!hasRequiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-juv-cream-white to-white">
        <Card className="max-w-md mx-auto bg-white border-juv-rose-clay/30">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-juv-rose-clay/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-juv-terracotta" />
            </div>
            <CardTitle className="text-juv-deep-chestnut font-juv-heading">Access Restricted</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-juv-deep-chestnut/70">
              You don't have permission to access this area. 
              {userRole && (
                <span className="block mt-2 text-sm">
                  Your current role: <span className="font-medium capitalize">{userRole}</span>
                </span>
              )}
            </p>
            <p className="text-sm text-juv-deep-chestnut/60">
              Required roles: {allowedRoles.map(role => role.charAt(0).toUpperCase() + role.slice(1)).join(', ')}
            </p>
            <Button 
              onClick={() => window.history.back()}
              className="w-full bg-juv-gradient-primary hover:shadow-glow transition-all duration-300"
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
